import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Api from '../Api/Api.json';
import { VictoryAxis, VictoryChart, VictoryLine, VictoryBar } from 'victory';

const SleepDurationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const SleepScoreIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l9 9 9-9a5.5 5.5 0 0 0 0-7.8z" />
    <polyline points="9 11 11 13 15 9" />
  </svg>
);

const SleepStartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polygon points="10,8 16,12 10,16 10,8" />
  </svg>
);

const METRIC_GOALS = {
  duration: '8.0 hours',
  deep_duration: '2.0 hours',
  rem_duration: '2.0 hours',
  light_duration: '4.0 hours',
  score: '90 points',
  start: '10:00 PM',
};

const getValidYValues = (dataList) => {
  if (!dataList?.length) return [];
  return dataList
    .filter((item) => item?.y != null && !Number.isNaN(Number(item.y)))
    .map((item) => Number(item.y));
};

const getLastGraphValue = (dataList) => {
  if (!dataList?.length) return null;
  for (let i = dataList.length - 1; i >= 0; i -= 1) {
    const y = dataList[i]?.y;
    if (y != null && !Number.isNaN(Number(y))) return Number(y);
  }
  return null;
};

const getAverageGraphValue = (dataList) => {
  const values = getValidYValues(dataList);
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const formatDurationValue = (value) =>
  value == null ? 'Not available' : Number(value).toFixed(1);

const formatScoreValue = (value) =>
  value == null ? 'Not available' : String(Math.round(Number(value)));

const formatSleepStartValue = (value) =>
  value == null ? 'Not available' : `${Number(value)}:00`;

const formatSleepStartAverage = (value) => {
  if (value == null) return 'Not available';
  const totalMinutes = Math.round(Number(value) * 60);
  const hours24 = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const displayHour = hours24 % 12 || 12;
  return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
};

const getTheme = (variant) => {
  if (variant === 'owner') {
    return {
      headerTitle: 'text-2xl font-bold text-gray-800 mb-2',
      headerSub: 'text-gray-600',
      card: 'bg-white rounded-lg shadow-md border border-gray-200',
      cardBodyText: 'text-gray-600',
      valueText: 'text-gray-900',
      labelText: 'text-gray-600',
      borderTop: 'border-gray-200',
      axisStroke: '#e5e7eb',
      tickFill: '#6b7280',
      tickFontSize: 14,
      gridStroke: '#f3f4f6',
    };
  }
  return {
    headerTitle: 'text-2xl font-bold text-white mb-2',
    headerSub: 'text-white',
    card: 'bg-[#161B22] rounded-lg shadow-md border border-[#30363D]',
    cardBodyText: 'text-white',
    valueText: 'text-white',
    labelText: 'text-white',
    borderTop: 'border-gray-100',
    axisStroke: '#8B949E',
    tickFill: '#F0F6FC',
    tickFontSize: 23,
    gridStroke: '#8B949E',
  };
};

const SleepAnalysisContent = ({ patientId, variant = 'default', showHeader = true }) => {
  const theme = getTheme(variant);
  const [sleepDurationList, setSleepDurationList] = useState([{}]);
  const [deepSleepDurationList, setDeepSleepDurationList] = useState([{}]);
  const [remSleepDurationList, setRemSleepDurationList] = useState([{}]);
  const [lightSleepDurationList, setLightSleepDurationList] = useState([{}]);
  const [sleepStartList, setSleepStartList] = useState([{}]);
  const [sleepScoreList, setSleepScoreList] = useState([{}]);
  const [dayNameList, setDayNameList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const transformDataForChart = (dataList) => {
    if (!dataList || dataList.length === 0) return [];
    return dataList.map((item, index) => ({
      x: index + 1,
      y: item.y,
      date: item.date,
      originalX: item.x,
    }));
  };

  const transformedSleepDurationList = transformDataForChart(sleepDurationList);
  const transformedDeepSleepDurationList = transformDataForChart(deepSleepDurationList);
  const transformedRemSleepDurationList = transformDataForChart(remSleepDurationList);
  const transformedLightSleepDurationList = transformDataForChart(lightSleepDurationList);
  const transformedSleepScoreList = transformDataForChart(sleepScoreList);
  const transformedSleepStartList = transformDataForChart(sleepStartList);

  useEffect(() => {
    if (!patientId) return;

    setIsLoading(true);
    const headers = { headers: { 'Content-Type': 'application/json' } };
    const url = Api.FITNESS_API;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const requestData = {
      task: 'getSemionWellnessData',
      id: patientId,
      timezone: userTimezone,
    };

    axios
      .post(url, requestData, headers)
      .then((result) => {
        if (result.data) {
          setSleepDurationList(result.data.sleepDurationList);
          setDeepSleepDurationList(result.data.deepSleepDurationList);
          setRemSleepDurationList(result.data.remSleepDurationList);
          setLightSleepDurationList(result.data.lightSleepDurationList);
          setSleepStartList(result.data.sleepStartList);
          setSleepScoreList(result.data.sleepScoreList);
          setDayNameList(result.data.dayNameList);
        }
      })
      .finally(() => setIsLoading(false));
  }, [patientId]);

  const getDataListForMetric = (metricId) => {
    switch (metricId) {
      case 'duration': return sleepDurationList;
      case 'deep_duration': return deepSleepDurationList;
      case 'rem_duration': return remSleepDurationList;
      case 'light_duration': return lightSleepDurationList;
      case 'score': return sleepScoreList;
      case 'start': return sleepStartList;
      default: return [];
    }
  };

  const getMetricStats = (metricId) => {
    const dataList = getDataListForMetric(metricId);
    const lastValue = getLastGraphValue(dataList);
    const averageValue = getAverageGraphValue(dataList);

    if (metricId === 'score') {
      return {
        currentValue: formatScoreValue(lastValue),
        averageDisplay: averageValue == null ? 'Not available' : `${Math.round(averageValue)} points`,
        unit: 'points',
      };
    }
    if (metricId === 'start') {
      return {
        currentValue: formatSleepStartValue(lastValue),
        averageDisplay: formatSleepStartAverage(averageValue),
        unit: 'time',
      };
    }
    return {
      currentValue: formatDurationValue(lastValue),
      averageDisplay: averageValue == null ? 'Not available' : `${averageValue.toFixed(1)} hours`,
      unit: 'hours',
    };
  };

  const sleepMetrics = useMemo(
    () => [
      { id: 'duration', name: 'Sleep Duration', description: 'Track your daily sleep duration and rest patterns', color: 'bg-[#58A6FF]', icon: <SleepDurationIcon />, ...getMetricStats('duration'), goal: METRIC_GOALS.duration },
      { id: 'deep_duration', name: 'Deep Sleep Duration', description: 'Track your daily deep sleep duration', color: 'bg-[#58A6FF]', icon: <SleepDurationIcon />, ...getMetricStats('deep_duration'), goal: METRIC_GOALS.deep_duration },
      { id: 'rem_duration', name: 'REM Sleep Duration', description: 'Track your daily REM sleep duration', color: 'bg-[#58A6FF]', icon: <SleepDurationIcon />, ...getMetricStats('rem_duration'), goal: METRIC_GOALS.rem_duration },
      { id: 'light_duration', name: 'Light Sleep Duration', description: 'Track your daily light sleep duration', color: 'bg-[#58A6FF]', icon: <SleepDurationIcon />, ...getMetricStats('light_duration'), goal: METRIC_GOALS.light_duration },
      { id: 'score', name: 'Sleep Score', description: 'Monitor your sleep quality and recovery metrics', color: 'bg-[#58A6FF]', icon: <SleepScoreIcon />, ...getMetricStats('score'), goal: METRIC_GOALS.score },
      { id: 'start', name: 'Sleep Start', description: 'Track when you go to sleep each night', color: 'bg-[#58A6FF]', icon: <SleepStartIcon />, ...getMetricStats('start'), goal: METRIC_GOALS.start },
    ],
    [sleepDurationList, deepSleepDurationList, remSleepDurationList, lightSleepDurationList, sleepScoreList, sleepStartList]
  );

  const getDurationTransformedList = (metricId) => {
    switch (metricId) {
      case 'duration': return transformedSleepDurationList;
      case 'deep_duration': return transformedDeepSleepDurationList;
      case 'rem_duration': return transformedRemSleepDurationList;
      case 'light_duration': return transformedLightSleepDurationList;
      default: return [];
    }
  };

  const axisStyle = {
    axis: { stroke: theme.axisStroke },
    tickLabels: { fill: theme.tickFill, fontSize: theme.tickFontSize, padding: 3 },
    grid: variant === 'owner' ? { stroke: theme.gridStroke, strokeWidth: 0.5 } : null,
  };

  const dependentAxisStyle = {
    axis: { stroke: theme.axisStroke },
    tickLabels: { fill: theme.tickFill, fontSize: theme.tickFontSize, padding: 3 },
    grid: { stroke: theme.gridStroke, strokeWidth: 0.5, strokeDasharray: variant === 'owner' ? undefined : 'none' },
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className={`ml-3 ${variant === 'owner' ? 'text-gray-600' : 'text-white'}`}>Loading sleep data...</span>
      </div>
    );
  }

  return (
    <>
      {showHeader && (
        <div className="mb-8">
          <h2 className={theme.headerTitle}>Sleep Analysis</h2>
          <p className={theme.headerSub}>Monitor sleep patterns and quality</p>
        </div>
      )}

      {sleepMetrics.map((metric) => (
        <div key={metric.id} className="mb-8">
          <div className={`${theme.card} overflow-hidden`}>
            <div className={`${metric.color} p-6 text-white`}>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">{metric.icon}</div>
                <div>
                  <h3 className="text-xl font-semibold">{metric.name}</h3>
                  <p className="text-sm opacity-90">Sleep Data</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className={`${theme.cardBodyText} text-sm leading-relaxed mb-4`}>{metric.description}</p>
              <div className="flex items-center justify-between">
                <div>
                  <span className={`text-2xl font-bold ${theme.valueText}`}>{metric.currentValue}</span>
                  {metric.currentValue !== 'Not available' && (
                    <span className={`${theme.valueText} ml-1`}>{metric.unit}</span>
                  )}
                </div>
                <div className={`text-sm ${theme.labelText}`}>Today</div>
              </div>
              <div className={`mt-4 pt-4 border-t ${theme.borderTop}`}>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={theme.labelText}>Average</span>
                    <div className={`font-medium ${theme.valueText}`}>{metric.averageDisplay}</div>
                  </div>
                  <div>
                    <span className={theme.labelText}>Goal</span>
                    <div className={`font-medium ${theme.valueText}`}>{metric.goal}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(metric.id === 'duration' || metric.id === 'deep_duration' || metric.id === 'rem_duration' || metric.id === 'light_duration') && (
            <div className="h-48 w-full overflow-hidden mt-4 px-[5px]">
              <VictoryChart domain={{ y: [0, 12] }} height={180} padding={{ top: 20, bottom: 40, left: 50, right: 20 }} style={{ parent: { width: '100%' } }}>
                <VictoryAxis tickValues={[1, 2, 3, 4, 5, 6, 7]} tickFormat={(t) => dayNameList[t - 1] || ''} style={axisStyle} />
                <VictoryAxis dependentAxis tickValues={[0, 2, 4, 6, 8, 10, 12]} style={dependentAxisStyle} />
                <VictoryBar data={getDurationTransformedList(metric.id).filter((d) => d.y !== null)} size={3} style={{ data: { fill: '#3b82f6' } }} />
                <VictoryLine data={getDurationTransformedList(metric.id).filter((d) => d.y !== null)} interpolation="natural" size={3} style={{ data: { stroke: '#ef4444', strokeWidth: 2, strokeLinecap: 'round' } }} />
              </VictoryChart>
            </div>
          )}

          {metric.id === 'score' && (
            <div className="h-48 w-full overflow-hidden mt-4 px-[5px]">
              <VictoryChart domain={{ y: [50, 100] }} height={180} padding={{ top: 20, bottom: 40, left: 50, right: 20 }} style={{ parent: { width: '100%' } }}>
                <VictoryAxis tickValues={[1, 2, 3, 4, 5, 6, 7]} tickFormat={(t) => dayNameList[t - 1] || ''} style={axisStyle} />
                <VictoryAxis dependentAxis tickValues={[60, 70, 80, 90, 100]} style={dependentAxisStyle} />
                <VictoryBar data={transformedSleepScoreList.filter((d) => d.y !== null)} size={3} style={{ data: { fill: '#10b981' } }} />
                <VictoryLine data={transformedSleepScoreList.filter((d) => d.y !== null)} interpolation="natural" size={3} style={{ data: { stroke: '#ef4444', strokeWidth: 2, strokeLinecap: 'round' } }} />
              </VictoryChart>
            </div>
          )}

          {metric.id === 'start' && (
            <div className="h-48 w-full overflow-hidden mt-4 px-[5px] mb-4">
              <VictoryChart domain={{ y: [0, 12] }} height={180} padding={{ top: 20, bottom: 40, left: 50, right: 20 }} style={{ parent: { width: '100%' } }}>
                <VictoryAxis tickValues={[1, 2, 3, 4, 5, 6, 7]} tickFormat={(t) => dayNameList[t - 1] || ''} style={axisStyle} />
                <VictoryAxis
                  dependentAxis
                  tickValues={[0, 2, 4, 6, 8, 10, 12]}
                  tickFormat={(t) => {
                    if (t === 0) return '12:00 AM';
                    if (t === 12) return '12:00 PM';
                    if (t > 12) return `${t - 12}:00 PM`;
                    return `${t}:00 AM`;
                  }}
                  style={dependentAxisStyle}
                />
                <VictoryBar data={transformedSleepStartList.filter((d) => d.y !== null)} size={3} style={{ data: { fill: '#8b5cf6' } }} />
                <VictoryLine data={transformedSleepStartList.filter((d) => d.y !== null)} interpolation="natural" size={3} style={{ data: { stroke: '#ef4444', strokeWidth: 2, strokeLinecap: 'round' } }} />
              </VictoryChart>
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default SleepAnalysisContent;
